import { Form } from 'antd';
import cloneDeep from 'lodash/cloneDeep';
import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BackOfficeDrawer from 'components/drawers/BackOfficeDrawer';
import BackOfficeUserForm from 'components/forms/BackOfficeUserForm';
import BackOfficeUserLoadingGate from 'components/loadingGates/BackOfficeUserLoadingGate';
import BackOfficeUserInvitationsRequest from 'components/requests/BackOfficeUserInvitationsRequest';
import BackOfficeUserRequest from 'components/requests/BackOfficeUserRequest';
import BackOfficeInvitationsActionType from 'store/modules/backOfficeInvitations/constants';
import useBackOfficeUser from 'store/modules/backOfficeUser/hooks';
import { editBackOfficeUser } from 'store/modules/backOfficeUsers/actions';
import BackOfficeUsersActionType from 'store/modules/backOfficeUsers/constants';
import clearErrors from 'store/modules/error/actions';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { appendValuesToQueryString } from 'store/modules/routerUtils/actions';
import { selectRouterQueryStringSelectedUser } from 'store/modules/routerUtils/selectors';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import User from 'types/models/user';
import { checkIsInternalAdmin } from 'utils/AccessControl/check-is-internal-admin';

const actions = [
  BackOfficeUsersActionType.EDIT_BACK_OFFICE_USER_REQUEST,
  BackOfficeInvitationsActionType.ADD_BACK_OFFICE_INVITATION_REQUEST,
  BackOfficeInvitationsActionType.REMOVE_BACK_OFFICE_INVITATION_REQUEST
];

function BackOfficeUserEditDrawer(): JSX.Element {
  const [form] = Form.useForm<Partial<User>>();
  const userId = useSelector(selectRouterQueryStringSelectedUser);

  const visible = !!userId;
  const backOfficeUserData = useBackOfficeUser();

  const initialValues = useMemo(
    () => cloneDeep(backOfficeUserData),
    [backOfficeUserData]
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (visible) dispatch(clearErrors(actions));
  }, [dispatch, visible]);

  const handleClose = () => {
    dispatch(
      appendValuesToQueryString({
        selectedUser: undefined
      })
    );
  };

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        if (userId) {
          if (values && values.memberships) {
            // eslint-disable-next-line no-param-reassign
            values.memberships = values.memberships.map(
              (m) => {
                const membership = m;

                delete membership.enterprise;

                return membership;
              }
            );
          }
          dispatch(editBackOfficeUser(userId, values));
        }
      })
      .catch(() => {
        // Ignoring promise rejection
      });
  };

  const canEditInternalUser = useMyUserHasInternalPermission({
    resource: ProtectedResource.INTERNAL_USER,
    action: ResourceAction.UPDATE
  });
  const canEditEnterpriseUser = useMyUserHasInternalPermission({
    resource: ProtectedResource.ENTERPRISE_USER,
    action: ResourceAction.UPDATE
  });
  const entityIsInternalUser = backOfficeUserData && checkIsInternalAdmin(backOfficeUserData);
  const entityIsEnterpriseUser = backOfficeUserData && !checkIsInternalAdmin(backOfficeUserData);
  const hasPermission =
    (canEditInternalUser && entityIsInternalUser) || (canEditEnterpriseUser && entityIsEnterpriseUser);

  return (
    <BackOfficeDrawer
      visible={visible}
      backOfficeSection={hasPermission ? 'Edit User' : 'User Details'}
      actions={hasPermission ? actions : []}
      handleSave={hasPermission ? handleSave : undefined}
      handleClose={handleClose}
      noFooter={!hasPermission}
    >
      <BackOfficeUserRequest userId={userId} />
      <BackOfficeUserInvitationsRequest email={initialValues?.email} />
      <BackOfficeUserLoadingGate userId={userId}>
        <BackOfficeUserForm
          form={form}
          initialValues={initialValues || undefined}
          readOnly={!hasPermission}
        />
      </BackOfficeUserLoadingGate>
    </BackOfficeDrawer>
  );
}

export default memo(BackOfficeUserEditDrawer);
